import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { WebsiteSettings } from '@app/core/interfaces/website-settings.interface';
import { MetaTagsInfoInterface } from '@core/interfaces/meta-tags-info.interface';

@Injectable()
export class GoogleService {
  private ids = {
    gaScript: 'ga-script-1',
    gaScript2: 'ga-script-2',
    gtmScript: 'gtm-script',
    gtmNoscript: 'gtm-noscript',
  };

  /**
   * AT-9146: identyfikatory GA/GTM trafiają do wnętrza skryptów wstawianych przez
   * `insertAdjacentHTML`, więc muszą mieć dokładnie format identyfikatora Google.
   * Wartość spoza formatu jest ignorowana (nie ładujemy wtedy żadnego skryptu).
   */
  private static readonly TRACKING_ID_FORMAT = /^[A-Za-z0-9_-]{1,32}$/;

  constructor(
    @Inject(DOCUMENT) private document: any,
  ) {
  }

  setGoogleScripts(websiteSettings: WebsiteSettings | MetaTagsInfoInterface) {
    this.loadGoogleTagManager(websiteSettings.googleTagManagerId);
    this.loadGoogleAnalytics(websiteSettings.googleAnalyticsId);
  }

  private loadGoogleAnalytics(trackingID: string): void {
    if (!GoogleService.isValidTrackingId(trackingID)) {
      return;
    }

    this.removeOldNodeById('script', this.ids.gaScript, this.document.head);
    this.removeOldNodeById('script', this.ids.gaScript2, this.document.head);


    // zaraz po rozpoczeciu <head>
    const gaScript = this.document.createElement('script');
    gaScript.id = this.ids.gaScript;
    gaScript.setAttribute('async', 'true');
    gaScript.setAttribute('src', `https://www.googletagmanager.com/gtag/js?id=${trackingID}`);

    // zaraz po rozpoczeciu <head>
    const gaScript2 = this.document.createElement('script');
    gaScript2.id = this.ids.gaScript2;
    gaScript2.setAttribute('async', 'true');
    gaScript2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());

      gtag('config', '${trackingID}');
    `;

    // this._renderer2.appendChild()
    this.document.head.insertAdjacentHTML('afterbegin', `${gaScript.outerHTML} ${gaScript2.outerHTML}`);
  }

  private static isValidTrackingId(trackingID: unknown): trackingID is string {
    return typeof trackingID === 'string' && GoogleService.TRACKING_ID_FORMAT.test(trackingID);
  }

  private loadGoogleTagManager(trackingID: string): void {
    if (!GoogleService.isValidTrackingId(trackingID)) {
      return;
    }

    this.removeOldNodeById('script', this.ids.gtmScript, this.document.head);
    this.removeOldNodeById('noscript', this.ids.gtmNoscript, this.document.body);

    const gtmScriptHTML = `
      <script id="${this.ids.gtmScript}" async>
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer', '${trackingID}');
      </script>
    `;

    this.document.head.insertAdjacentHTML('afterbegin', gtmScriptHTML);

    const gtmNoscriptHTML = `
      <noscript id="${this.ids.gtmNoscript}">
        <iframe src="https://www.googletagmanager.com/ns.html?id=${trackingID}"
                height="0" width="0" style="display:none;visibility:hidden"></iframe>
      </noscript>
    `;

    this.document.body.insertAdjacentHTML('afterbegin', gtmNoscriptHTML);
  }


  private removeOldNodeById(tagName: string, id: string, parent?: HTMLElement) {
    parent = parent ?? this.document;
    const nodes = parent.getElementsByTagName(tagName);
    try {
      for (const s of nodes as any) {
        if (s.id === id) {
          s.remove();
        }
      }
    } catch (e) {
      // try-catch for working on server purposes
    }
  }
}
